import {ServiceSubscription} from '@frontapp/editor-html/lib/react';
import {RuleBundleType} from '@frontapp/workflows/rules';
import React, {FC, RefObject, useEffect, useMemo, useRef, useState} from 'react';
import {Key} from 'ts-key-enum';

import {globalWindow} from '../../../../libs/shared/core/src/helpers/browser/browserHelpers';
import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {useDebounce} from '../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {useEventHandler} from '../../../../libs/shared/core/src/helpers/react/hooks/useEventHandler';
import {FrontKeyboardEvent} from '../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {
  buildCancelTokenSource,
  CancelTokenSource,
} from '../../../../libs/shared/core/src/helpers/utilities/asyncCancelHelpers';
import {TeammateModel} from '../../../../libs/shared/core/src/models/api/teammateModel';
import {TeamModel} from '../../../../libs/shared/core/src/models/api/teamModel';
import {fetchApiAsync} from '../../../../libs/shared/core/src/services/http/http';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../libs/shared/core/src/services/tracking/tracking';
import {ResourceMap} from '../../../../libs/shared/core/src/store/commonAppTypes';
import {useAppSelector} from '../../../helpers/appReduxHelpers';
import {
  ApiSearchSettingsPayload,
  SettingsSearchItemModel,
  SettingsSearchItemTypesEnum,
  SettingsSearchPayloadModel,
} from '../../../models/api/settingsSearchModel';
import {SettingsTagTabsEnum} from '../../../models/url/settingsUrlModel';
import {
  isNamespaceSelectResourceType,
  NamespaceSelectResourceTypesEnum,
} from '../../../settings/home/settingsHomeTypes';
import {makeSettingsUrlFromPathId} from '../../../settings/menu/settingsHelpers';
import {RuleParametersDependencies} from '../../../settings/rules/ruleConditionModel';
import {computeIsRuleRecipeBundle} from '../../../settings/rules/ruleHelpers';
import {getCompany} from '../../../store/data/company/companySelectors';
import {SearchBar, SearchBarStatesEnum} from '../../common/searchBar/searchBar';
import {useSearchBarEditor} from '../../common/searchBar/searchBarEditor';
import {
  SettingsSearchSeeAllHandler,
  SettingsSearchSelectItemHandler,
  SettingsSearchTrackSearchQueryHandler,
  SettingsSearchTrackSearchResultHandler,
} from './settingsSearchCommonTypes';
import {SettingsSearchDropdown} from './settingsSearchDropdown';

/*
 * Constants.
 */

enum SettingsSearchBarState {
  LOADING = 'loading',
  LOADED = 'loaded',
  ERROR = 'error',
}

/*
 * Props.
 */

interface SettingsSearchBarProps {
  navigateToLink: (arg: string) => void;
  teamsMap: ResourceMap<TeamModel>;
  user: TeammateModel;
  companyUrl: string;
  settingsTypeTrackingField: string | undefined;
  teamsUserIsAdminOf: ReadonlyArray<TeamModel>;
  ruleParametersDependencies: RuleParametersDependencies;
}

/*
 * Component.
 */

export const SettingsSearchBar: FC<SettingsSearchBarProps> = (props) => {
  const {navigateToLink, settingsTypeTrackingField} = props;

  const company = useAppSelector(getCompany);
  const editor = useSearchBarEditor('');
  const searchBarWrapperRef = useRef<HTMLDivElement>(null);
  const [searchResults, setSearchResults] = useState<SettingsSearchPayloadModel | undefined>(undefined);
  const [query, setQuery] = useState('');
  const [searchBarState, setSearchBarState] = useState(SettingsSearchBarState.LOADED);

  const limitedSearchResults = useMemo(() => {
    if (!searchResults) {
      return searchResults;
    }

    return limitNumbersOfResults(searchResults);
  }, [searchResults]);

  const trackSearchQuery: SettingsSearchTrackSearchQueryHandler = (value) => {
    trackInterfaceEvent(InterfaceEventsEnum.SETTINGS_SEARCH_QUERY, {
      ...value,
      search_type: 'global',
      settings_type: settingsTypeTrackingField,
    });
  };

  const trackSearchResultClick: SettingsSearchTrackSearchResultHandler = (value) => {
    trackInterfaceEvent(InterfaceEventsEnum.SETTINGS_SEARCH_CLICK, {
      ...value,
      search_type: 'global',
      settings_type: settingsTypeTrackingField,
    });
  };

  const reportSearchQuery = useDebounce((latestQuery: string, resultsCount: number) => {
    trackSearchQuery({
      query: latestQuery,
      results_count: resultsCount,
    });
  }, 500);

  const onSeeAllClick: SettingsSearchSeeAllHandler = (resourceType) => {
    trackSearchResultClick({
      click_type: 'shortcut',
      resource_id: null,
      shortcut_type: resourceType,
    });

    const seeAllUrlOrResourceType = makeSeeAllUrl(resourceType, props.user.namespace);

    if (isNamespaceSelectResourceType(seeAllUrlOrResourceType)) {
      return;
    }

    if (!seeAllUrlOrResourceType) {
      return;
    }

    navigateToLink(seeAllUrlOrResourceType);
    setSearchResults(undefined);
    onClear();
  };

  const onSelectItem: SettingsSearchSelectItemHandler = (payload, index) => {
    trackSearchResultClick({
      click_type: payload.resourceType,
      ...getItemValueObject(payload, index),
    });

    const selectItemUrl = makeSelectItemUrl(payload, index, props.user.namespace, props.teamsUserIsAdminOf);

    if (isNamespaceSelectResourceType(selectItemUrl)) {
      return;
    }

    if (!selectItemUrl) {
      return;
    }

    navigateToLink(selectItemUrl);
    setSearchResults(undefined);
    onClear();
  };

  const onKeyDown = (event: FrontKeyboardEvent) => {
    if (event.key === Key.Enter) {
      event.preventDefault();
      const firstResult = limitedSearchResults?.[0];
      if (firstResult) {
        onSelectItem(firstResult, 0);
      }
      return;
    }

    if (event.key === Key.Escape) {
      event.preventDefault();
      editor.services.focus.blur();
    }
  };

  const onClear = () => {
    editor.services.text.setText('');
    setQuery('');
  };

  // On a click on the box, focus the input.
  const onBoxClick = () => editor.services.focus.focus();

  const cancelTokenSourceRef = useRef<CancelTokenSource>();

  function maybeCancelRequest() {
    const currentCancelTokenSource = cancelTokenSourceRef.current;
    return currentCancelTokenSource && currentCancelTokenSource.cancel();
  }

  const querySearch = async (latestQuery: string) => {
    maybeCancelRequest();

    const cancelTokenSource = buildCancelTokenSource();
    cancelTokenSourceRef.current = cancelTokenSource;

    return fetchApiAsync(
      ApiSearchSettingsPayload,
      `${company.url}/search_settings/${encodeURIComponent(latestQuery)}`,
      {
        cancelToken: cancelTokenSource.token,
      },
    );
  };

  const onInputChange = useEventHandler(
    useDebounce(async (latestQuery: string) => {
      setQuery(latestQuery);

      if (!latestQuery) {
        setSearchResults(undefined);
        return;
      }

      const loadingTimeout = globalWindow.setInstrumentedTimeout(() => {
        setSearchBarState(SettingsSearchBarState.LOADING);
      }, 500);

      try {
        const results = await querySearch(latestQuery);

        reportSearchQuery(
          latestQuery,
          results.map((i) => i.items.length).reduce((a, b) => a + b, 0),
        );
        globalWindow.clearTimeout(loadingTimeout);
        setSearchBarState(SettingsSearchBarState.LOADED);
        setSearchResults(results);
      } catch {
        setSearchBarState(SettingsSearchBarState.ERROR);
      }
    }, 200),
  );

  useEffect(() => {
    const subscription = editor.services.text.changes$.subscribe(() => {
      onInputChange(editor.services.text.text);
    });

    return () => subscription.unsubscribe();
  }, [editor, onInputChange]);

  return (
    <ServiceSubscription source={editor.services.focus}>
      {({isActive}) => (
        <>
          <SearchBar
            editor={editor}
            outerRef={searchBarWrapperRef}
            state={isActive ? SearchBarStatesEnum.FOCUSED : SearchBarStatesEnum.IDLE}
            onKeyDown={onKeyDown}
            renderPlaceholder={renderPlaceholder}
            onExitClick={onClear}
            onClick={onBoxClick}
          />
          {maybeRenderSuggestionsDropdown(
            props,
            limitedSearchResults,
            isActive,
            searchBarWrapperRef,
            query,
            searchBarState,
            onSeeAllClick,
            onSelectItem,
          )}
        </>
      )}
    </ServiceSubscription>
  );
};

/*
 * Helpers.
 */

function renderPlaceholder() {
  return <Trans id="settings-search-bar-placeholder">Search teammates, inboxes, rules, tags, and more</Trans>;
}

function maybeRenderSuggestionsDropdown(
  props: SettingsSearchBarProps,
  searchResults: SettingsSearchPayloadModel | undefined,
  isFocused: boolean,
  anchor: RefObject<HTMLDivElement>,
  query: string,
  searchBarState: SettingsSearchBarState,
  onSeeAllClick: SettingsSearchSeeAllHandler,
  onSelectItem: SettingsSearchSelectItemHandler,
) {
  const isStillLoading = searchBarState === SettingsSearchBarState.LOADING;
  const hasError = searchBarState === SettingsSearchBarState.ERROR;

  if (!isFocused || !query || (!searchResults && !isStillLoading && !hasError)) {
    return null;
  }

  return (
    <SettingsSearchDropdown
      anchor={anchor}
      isStillLoading={isStillLoading}
      hasError={hasError}
      ruleParametersDependencies={props.ruleParametersDependencies}
      searchResults={searchResults}
      user={props.user}
      onSeeAllClick={onSeeAllClick}
      onSelectItem={onSelectItem}
      teamsMap={props.teamsMap}
    />
  );
}

//  This method makes sure we never display more than 14 items.
const ITEMS_UPPER_LIMIT = 14;
function limitNumbersOfResults(results: SettingsSearchPayloadModel) {
  // Compute a maximum number of items.
  const MAX_ITEMS = Math.max(ITEMS_UPPER_LIMIT - results.length, results.length);

  // 1. Compute the total amount of items.
  let total = results.map((i) => i.items.length).reduce((a, b) => a + b, 0);
  const finalResults = [...results];

  // 2. Loop as long as the total is above our limit
  while (total > MAX_ITEMS) {
    // eslint-disable-next-line no-loop-func
    finalResults.forEach((payload, index) => {
      // If we have less than two items, no need to cut.
      if (payload.items.length < 2) {
        return;
      }

      // Create a new array of items with the last item removed.
      const newItems = [...payload.items];
      newItems.pop();
      total -= 1;

      // Insert our new array in the final array
      finalResults[index] = {
        ...payload,
        items: newItems,
      } as SettingsSearchItemModel;
    });
  }

  return finalResults;
}

function makeSeeAllUrl(
  resourceType: SettingsSearchItemTypesEnum,
  userNamespace: string,
): NamespaceSelectResourceTypesEnum | string | undefined {
  const resourceTypeToResourceEnum = {
    [SettingsSearchItemTypesEnum.ANSWER]: NamespaceSelectResourceTypesEnum.ANSWERS,
    [SettingsSearchItemTypesEnum.INBOX]: NamespaceSelectResourceTypesEnum.INBOXES,
    [SettingsSearchItemTypesEnum.ROLE]: NamespaceSelectResourceTypesEnum.ROLES,
    [SettingsSearchItemTypesEnum.RULE]: NamespaceSelectResourceTypesEnum.RULES,
    [SettingsSearchItemTypesEnum.SIGNATURE]: NamespaceSelectResourceTypesEnum.SIGNATURES,
    [SettingsSearchItemTypesEnum.TAG]: NamespaceSelectResourceTypesEnum.TAGS,
    [SettingsSearchItemTypesEnum.CUSTOM_VIEW]: NamespaceSelectResourceTypesEnum.CUSTOM_VIEWS,
  };

  switch (resourceType) {
    case SettingsSearchItemTypesEnum.ANSWER:
    case SettingsSearchItemTypesEnum.CUSTOM_VIEW:
    case SettingsSearchItemTypesEnum.INBOX:
    case SettingsSearchItemTypesEnum.ROLE:
    case SettingsSearchItemTypesEnum.SIGNATURE:
    case SettingsSearchItemTypesEnum.RULE:
    case SettingsSearchItemTypesEnum.TAG:
      return resourceTypeToResourceEnum[resourceType];

    case SettingsSearchItemTypesEnum.INTEGRATION:
      return makeSettingsUrlFromPathId('settings.integrations.native.list');
    case SettingsSearchItemTypesEnum.IMPORT_ROUTE:
      return makeSettingsUrlFromPathId('settings.tools.imports.list');
    case SettingsSearchItemTypesEnum.PERSONAL_PREFERENCE:
      return makeSettingsUrlFromPathId('settings.myPreferences', {
        namespace: userNamespace,
      });
    case SettingsSearchItemTypesEnum.COMPANY_PREFERENCE:
      return makeSettingsUrlFromPathId('settings.companyPreferences.general', {}, {search: ''});
    case SettingsSearchItemTypesEnum.TEAM:
      return makeSettingsUrlFromPathId('settings.teams');
    case SettingsSearchItemTypesEnum.TEAMMATE:
      return makeSettingsUrlFromPathId('settings.teammates', {namespace: 'global'});
    case SettingsSearchItemTypesEnum.VACATION_RESPONDER:
      return makeSettingsUrlFromPathId('settings.schedules.time-off.list', {namespace: userNamespace});
    default:
      return undefined;
  }
}

function makeSelectItemUrl(
  payloadItem: SettingsSearchItemModel,
  itemIndex: number,
  userNamespace: string,
  teamsUserIsAdminOf: ReadonlyArray<TeamModel>,
): NamespaceSelectResourceTypesEnum | string | undefined {
  if (payloadItem.resourceType === SettingsSearchItemTypesEnum.INTEGRATION) {
    const item = payloadItem.items[itemIndex];
    return makeSettingsUrlFromPathId('settings.integrations.native.edit.details', {integration: item.type});
  }
  if (payloadItem.resourceType === SettingsSearchItemTypesEnum.INBOX) {
    const item = payloadItem.items[itemIndex];
    return makeSettingsUrlFromPathId('settings.inboxes.edit.overview', {
      namespace: item.inbox.namespace,
      inbox: item.inbox.id,
    });
  }

  if (payloadItem.resourceType === SettingsSearchItemTypesEnum.CUSTOM_VIEW) {
    const item = payloadItem.items[itemIndex];
    return makeSettingsUrlFromPathId('settings.custom-views.edit', {
      namespace: item.namespace,
      customView: item.id,
    });
  }

  if (payloadItem.resourceType === SettingsSearchItemTypesEnum.ROLE) {
    const item = payloadItem.items[itemIndex];
    return makeSettingsUrlFromPathId('settings.roles.edit.overview', {
      namespace: item.role.namespace,
      role: item.role.alias,
    });
  }
  if (payloadItem.resourceType === SettingsSearchItemTypesEnum.RULE) {
    const item = payloadItem.items[itemIndex];
    if (item.ruleBundleType === RuleBundleType.SLA) {
      return makeSettingsUrlFromPathId('settings.rules.recipes', {
        namespace: item.rule.namespace,
        recipeid: String(item.ruleBundleId),
      });
    }

    if (item.ruleBundleType === RuleBundleType.CSAT) {
      return makeSettingsUrlFromPathId('settings.rules.recipecsat', {
        namespace: item.rule.namespace,
        recipeid: String(item.ruleBundleId),
      });
    }

    return makeSettingsUrlFromPathId('settings.rules.ui.edit', {
      namespace: item.rule.namespace,
      ruleid: item.rule.id,
    });
  }
  if (payloadItem.resourceType === SettingsSearchItemTypesEnum.TEAM) {
    const item = payloadItem.items[itemIndex];
    return makeSettingsUrlFromPathId('settings.teams.edit', {team: item.team.id});
  }
  if (payloadItem.resourceType === SettingsSearchItemTypesEnum.SIGNATURE) {
    const item = payloadItem.items[itemIndex];
    return makeSettingsUrlFromPathId('settings.signatures.edit.overview', {
      signature: item.id,
      namespace: item.namespace,
    });
  }
  if (payloadItem.resourceType === SettingsSearchItemTypesEnum.TEAMMATE) {
    const item = payloadItem.items[itemIndex];
    return makeSettingsUrlFromPathId('settings.teammates.edit.overview', {
      teammate: item.alias,
      namespace: 'global',
    });
  }
  if (payloadItem.resourceType === SettingsSearchItemTypesEnum.TAG) {
    const item = payloadItem.items[itemIndex];
    if (item.tag.archivedAt) {
      return makeSettingsUrlFromPathId(
        'settings.new-tags',
        {
          namespace: item.tag.namespace,
        },
        {
          page: SettingsTagTabsEnum.ARCHIVED,
          search: item.tag.name,
        },
      );
    }

    return makeSettingsUrlFromPathId('settings.new-tags.edit', {
      tag: item.tag.id,
      namespace: item.tag.namespace,
    });
  }
  if (payloadItem.resourceType === SettingsSearchItemTypesEnum.IMPORT_ROUTE) {
    const item = payloadItem.items[itemIndex];
    return makeSettingsUrlFromPathId('settings.tools.imports.new', {
      importType: item.key.replace('Import', '').toLowerCase(),
    });
  }
  if (payloadItem.resourceType === SettingsSearchItemTypesEnum.COMPANY_PREFERENCE_ROUTE) {
    const item = payloadItem.items[itemIndex];
    if (item.key === 'singleSignOn') {
      return makeSettingsUrlFromPathId('settings.companyPreferences.sso', {});
    }
  }
  if (payloadItem.resourceType === SettingsSearchItemTypesEnum.COMPANY_PREFERENCE) {
    const item = payloadItem.items[itemIndex];
    return makeSettingsUrlFromPathId('settings.companyPreferences.general', {}, {search: item.title});
  }
  if (payloadItem.resourceType === SettingsSearchItemTypesEnum.COMPANY_SETTINGS_ROUTE) {
    const item = payloadItem.items[itemIndex];
    switch (item.key) {
      case 'billing': {
        return makeSettingsUrlFromPathId('settings.billing', {});
      }
      case 'deliverability': {
        return makeSettingsUrlFromPathId('settings.deliverability', {});
      }
      case 'loadBalancing': {
        if (teamsUserIsAdminOf.length > 1) {
          return NamespaceSelectResourceTypesEnum.LOAD_BALANCING;
        }
        return makeSettingsUrlFromPathId('settings.rules.ui.limits.list', {
          namespace: teamsUserIsAdminOf[0].namespace,
        });
      }
      default: {
        return undefined;
      }
    }
  }
  if (payloadItem.resourceType === SettingsSearchItemTypesEnum.PERSONAL_PREFERENCE) {
    const item = payloadItem.items[itemIndex];
    if (item.key === 'customSnoozes') {
      return makeSettingsUrlFromPathId('settings.snoozes.list', {namespace: userNamespace});
    }

    return makeSettingsUrlFromPathId(
      'settings.myPreferences',
      {
        namespace: userNamespace,
      },
      {
        search: item.title,
      },
    );
  }
  if (payloadItem.resourceType === SettingsSearchItemTypesEnum.SETTINGS_ROUTE) {
    const item = payloadItem.items[itemIndex];

    switch (item.key) {
      case 'myProfile': {
        return makeSettingsUrlFromPathId('settings.profile.overview', {});
      }
      case 'notifications': {
        return makeSettingsUrlFromPathId('settings.notifications', {});
      }
      default: {
        return undefined;
      }
    }
  }
  if (payloadItem.resourceType === SettingsSearchItemTypesEnum.TEAMMATE_ROUTE) {
    const item = payloadItem.items[itemIndex];
    if (item.key === 'activityExport') {
      if (teamsUserIsAdminOf.length > 1) {
        return NamespaceSelectResourceTypesEnum.ACTIVITY_EXPORT;
      }
      return makeSettingsUrlFromPathId('settings.teammates.export', {
        namespace: teamsUserIsAdminOf[0].namespace,
      });
    }
    if (item.key === 'userProvisioning') {
      if (teamsUserIsAdminOf.length > 1) {
        return NamespaceSelectResourceTypesEnum.USER_PROVISIONING;
      }
      return makeSettingsUrlFromPathId('settings.teammates.provisioning', {
        namespace: teamsUserIsAdminOf[0].namespace,
      });
    }
  }
  if (payloadItem.resourceType === SettingsSearchItemTypesEnum.CHANNEL) {
    const item = payloadItem.items[itemIndex];
    return makeSettingsUrlFromPathId('settings.inboxes.edit.channels.channel', {
      namespace: item.namespace,
      inbox: item.inboxId,
      channelId: item.id,
    });
  }
  if (payloadItem.resourceType === SettingsSearchItemTypesEnum.DELEGATION) {
    const item = payloadItem.items[itemIndex];
    return makeSettingsUrlFromPathId('settings.inboxes.delegations.edit', {
      namespace: userNamespace,
      delegation: item.delegation.id,
    });
  }

  return undefined;
}

function getItemValueObject(
  payloadItem: SettingsSearchItemModel,
  itemIndex: number,
): {resource_id: string | number | null; shortcut_type: string | null} {
  switch (payloadItem.resourceType) {
    case SettingsSearchItemTypesEnum.ANSWER:
    case SettingsSearchItemTypesEnum.CUSTOM_VIEW:
    case SettingsSearchItemTypesEnum.SIGNATURE:
    case SettingsSearchItemTypesEnum.CHANNEL:
    case SettingsSearchItemTypesEnum.TEAMMATE: {
      return {
        resource_id: payloadItem.items[itemIndex].id || null,
        shortcut_type: null,
      };
    }

    case SettingsSearchItemTypesEnum.RULE: {
      return {
        resource_id:
          (computeIsRuleRecipeBundle(payloadItem.items[itemIndex].ruleBundleType)
            ? payloadItem.items[itemIndex].ruleBundleId
            : payloadItem.items[itemIndex].rule.id) || null,
        shortcut_type: null,
      };
    }

    case SettingsSearchItemTypesEnum.TEAM: {
      return {
        resource_id: payloadItem.items[itemIndex].team.id,
        shortcut_type: null,
      };
    }
    case SettingsSearchItemTypesEnum.INBOX: {
      return {
        resource_id: payloadItem.items[itemIndex].inbox.id,
        shortcut_type: null,
      };
    }
    case SettingsSearchItemTypesEnum.TAG: {
      return {
        resource_id: payloadItem.items[itemIndex].tag.id,
        shortcut_type: null,
      };
    }
    case SettingsSearchItemTypesEnum.ROLE: {
      return {
        resource_id: payloadItem.items[itemIndex].role.alias,
        shortcut_type: null,
      };
    }
    case SettingsSearchItemTypesEnum.VACATION_RESPONDER: {
      return {
        resource_id: payloadItem.items[itemIndex].vacationResponder.id,
        shortcut_type: null,
      };
    }
    case SettingsSearchItemTypesEnum.SETTINGS_ROUTE:
    case SettingsSearchItemTypesEnum.TEAMMATE_ROUTE:
    case SettingsSearchItemTypesEnum.IMPORT_ROUTE:
    case SettingsSearchItemTypesEnum.PERSONAL_PREFERENCE:
    case SettingsSearchItemTypesEnum.COMPANY_PREFERENCE_ROUTE:
    case SettingsSearchItemTypesEnum.COMPANY_SETTINGS_ROUTE: {
      return {
        resource_id: null,
        shortcut_type: payloadItem.items[itemIndex].key,
      };
    }

    case SettingsSearchItemTypesEnum.INTEGRATION: {
      return {
        resource_id: null,
        shortcut_type: payloadItem.items[itemIndex].type,
      };
    }

    default: {
      return {
        resource_id: null,
        shortcut_type: null,
      };
    }
  }
}
